import Icon from 'antd/lib/icon';
import Tabs from 'antd/lib/tabs';
import * as React from 'react';
import { useCallback, useState } from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'src/Library/Types';
import { numberFormatted } from 'src/utils/utils';
import { getQuoteTotals, getVendorTypeFromQuoteField, VendorTypeLabels } from '../../../SE.constants';
import { QuoteType } from '../../../SE.interfaces';
import { SEQuoteSelectionContext } from './SEQuoteSelectionContext';
import { SEQuoteTaskTabsStyles } from './SEQuoteTaskTabs.styles';

const TabPane = Tabs.TabPane;

const styles = () => ({ ...SEQuoteTaskTabsStyles() });

/** Plain-data descriptor for one tab, aligned by index with the field node children. */
export interface SEQuoteTabMeta {
  key: string;
  fieldMeta?: { name?: string; masterCode?: string; code?: string };
}

interface SEQuoteTaskTabsProps extends StylesProps<ReturnType<typeof styles>> {
  /**
   * The per-vendor RFQ_QUOTE field nodes, one per tab. These MUST be passed as children (not via a
   * prop) so the field-group render pipeline (useFieldGroup `walk`/`injectProps`) can inject each
   * node's ref + field config — it only traverses `props.children`. Passing them through any other
   * prop leaves the fields un-hydrated and renders a blank task.
   */
  children?: React.ReactNode;
  /** Tab label/key metadata, aligned by index with `children`. */
  tabMeta?: SEQuoteTabMeta[];
  /**
   * The submitted L1 Deviation Reason (SMF425) + Deviation Remarks (SMF426), read from the saved
   * task fields. Rendered read-only beneath the footer on the approval view (SMT72) so the approver
   * sees the justification the ops user gave. Absent on a fresh SMT71 task (nothing submitted yet).
   */
  deviationReason?: string;
  deviationRemarks?: string;
}

const formatAmount = (amount: number): string =>
  numberFormatted(amount, { places: 2, thousandSeparated: true });

const tabLabel = (meta: SEQuoteTabMeta | undefined, index: number): string => {
  const vendorType = getVendorTypeFromQuoteField(meta?.fieldMeta);
  return (vendorType && VendorTypeLabels[vendorType]) || meta?.fieldMeta?.name || `Quote ${index + 1}`;
};

/** Selection map key for a tab — mirrors the vendor-type-first key SEQuotePanel reports. */
const tabSelectionKey = (meta: SEQuoteTabMeta | undefined): string =>
  getVendorTypeFromQuoteField(meta?.fieldMeta) || meta?.fieldMeta?.name || '';

/**
 * Task-level wrapper for the Quote Selection task (SMT71). Lays out each per-vendor RFQ_QUOTE field
 * (passed as children, aligned by index with `tabMeta`) as a tab, and renders a single combined
 * footer (Total Quote Amount / Deviation) outside the tabs.
 */
const SEQuoteTaskTabs = (props: SEQuoteTaskTabsProps): React.ReactElement => {
  const { classes, children, tabMeta = [], deviationReason, deviationRemarks } = props;
  const [selections, setSelections] = useState<Record<string, QuoteType | undefined>>({});

  const reportSelection = useCallback((fieldKey: string, quote: QuoteType | undefined) => {
    setSelections((prev) => {
      if (prev[fieldKey] === quote) return prev;
      return { ...prev, [fieldKey]: quote };
    });
  }, []);

  const selected = Object.values(selections).filter(Boolean) as QuoteType[];
  const totals = getQuoteTotals(selected);

  const tabs = React.Children.toArray(children);

  return (
    <SEQuoteSelectionContext.Provider value={{ reportSelection }}>
      <div className={classes.container}>
        <Tabs type="card">
          {tabs.map((node, index) => {
            const hasSelection = Boolean(selections[tabSelectionKey(tabMeta[index])]);
            return (
              <TabPane
                key={tabMeta[index]?.key || String(index)}
                tab={(
                  <span className={classes.tabLabel}>
                    {hasSelection && (
                      <Icon
                        type="check-circle"
                        theme="filled"
                        className={classes.tabSelectedIcon}
                      />
                    )}
                    {tabLabel(tabMeta[index], index)}
                  </span>
                )}
                forceRender
              >
                {node}
              </TabPane>
            );
          })}
        </Tabs>
        <div className={classes.footer}>
          <div className={classes.footerBlock}>
            <span className={classes.footerLabel}>Total Quote Amount</span>
            <span className={classes.footerValue}>INR {formatAmount(totals.totalQuoteAmount)}</span>
          </div>
          <div className={classes.footerBlock}>
            <span className={classes.footerLabel}>Total Deviation Amount</span>
            <span className={classes.footerValue}>INR {formatAmount(totals.totalDeviation)}</span>
          </div>
        </div>
        {(deviationReason || deviationRemarks) && (
          <div className={classes.deviationSummary}>
            {deviationReason && (
              <div className={classes.deviationRow}>
                <span className={classes.deviationLabel}>L1 Deviation Reason</span>
                <span className={classes.deviationValue}>{deviationReason}</span>
              </div>
            )}
            {deviationRemarks && (
              <div className={classes.deviationRow}>
                <span className={classes.deviationLabel}>Deviation Remarks</span>
                <span className={classes.deviationValue}>{deviationRemarks}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </SEQuoteSelectionContext.Provider>
  );
};

export default withStyles(styles)(SEQuoteTaskTabs);
